import React, { useContext, useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import {
  CompanyMutations,
  DeleteCompanyResponse,
  DeleteCompanyVariables,
} from '../../../../../graphql/company.graphql';
import { AlertModal } from '../../../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { t } from '../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { TextInput } from '../../../../components/TextInput';
import { Button, ButtonVariant } from '../../../../components/Button';
import { storeCurrentCompanyId } from '../../../../../util/token.util';
import { Routing } from '../../../../../util/routes.util';
import useMutation from '../../../../../hooks/useMutation';

interface DeleteCompanyModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function DeleteCompanyModal({ open, setOpen }: DeleteCompanyModalProps) {
  const { currentCompany } = useContext(CompanyContext);
  const navigate = useNavigate();

  const [companyNameValidation, setCompanyNameValidation] = useState('');

  const [deleteCompany, { loading }] = useMutation<DeleteCompanyResponse, DeleteCompanyVariables>(
    CompanyMutations.remove,
    {
      onCompleted: response => {
        storeCurrentCompanyId('');
        navigate(Routing.companies.overview());
        window.location.reload();
      },
    },
  );

  const handleDelete = () => {
    deleteCompany({
      variables: {
        company: currentCompany.forDelete(),
      },
    });
  };

  return (
    <AlertModal open={open} setOpen={setOpen} title={t(TranslationKey.deleteCompanyModalTitle).singular.upper}>
      <div className='body-light grid gap-4'>
        <p>{t(TranslationKey.deleteCompanyModalPrimaryText).singular.upper}</p>
        <ul className='list-inside list-disc'>
          <li>{t(TranslationKey.deleteCompanyModalListItemStockLocation).plural.upper}</li>
          <li>{t(TranslationKey.deleteCompanyModalListItemProduct).plural.upper}</li>
          <li>{t(TranslationKey.deleteCompanyModalListItemTasksAndOrders).singular.upper}</li>
          <li>{t(TranslationKey.deleteCompanyModalListItemBinsAndLots).singular.upper}</li>
          <li>{t(TranslationKey.deleteCompanyModalListItemHistoricalRecordsAndActivityLog).singular.upper}</li>
          <li>{t(TranslationKey.deleteCompanyModalListItemUserRolesAndPermissions).singular.upper}</li>
        </ul>
        <p className='text-error-500'>
          {t(TranslationKey.deleteCompanyModalActionCannotBeUndoneWarning).singular.upper}
        </p>
        <p>{t(TranslationKey.deleteCompanyModalConfirmationText).singular.upper}</p>
        <TextInput
          placeholder={currentCompany.name}
          label={t(TranslationKey.deleteCompanyModalValidateCompanyNameInputLabel).singular.upper}
          onChange={setCompanyNameValidation}
          value={companyNameValidation}
        />
        <div className='flex items-center justify-end gap-2'>
          <Button
            text={t(TranslationKey.cancel).singular.upper}
            variant={ButtonVariant.link}
            onClick={() => setOpen(false)}
            disabled={loading}
          />
          <Button
            text={t(TranslationKey.deleteCompany).singular.upper}
            disabled={currentCompany.name !== companyNameValidation}
            variant={ButtonVariant.danger}
            onClick={handleDelete}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
